import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Form, Input, Select, message, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import PageHeader from '../../components/PageHeader';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import AssetListCard from '../../components/AssetListCard';
import type { AssetListCardHandlers } from '../../components/AssetListCard';
import {
  getAssets,
  createAsset,
  updateAsset,
  deleteAsset,
  getOrganizations,
  Asset,
  Organization,
} from '../../services/adminService';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import ResponsiveModal from '../../components/ResponsiveModal';

const AssetList: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const sortAssets = (items: Asset[]) => [...items].sort((a, b) => a.name.localeCompare(b.name));

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const data = await getAssets();
      setAssets(sortAssets(data));
    } catch (err: any) {
      message.error(err.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchOrganizations();
  }, []);

  const handleCreate = async (values: any) => {
    setSubmitting(true);
    try {
      await createAsset({
        name: values.name,
        organizationId: values.organizationId,
        asset_type: values.asset_type,
      });
      message.success('Asset created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to create asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: Asset) => {
    setEditingAsset(record);
    editForm.setFieldsValue({
      name: record.name,
      asset_type: record.asset_type,
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!editingAsset) return;
    setSubmitting(true);
    try {
      await updateAsset(editingAsset.id, {
        name: values.name,
        asset_type: values.asset_type,
      });
      message.success('Asset updated successfully');
      setEditModalVisible(false);
      setEditingAsset(null);
      editForm.resetFields();
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to update asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAsset(id);
      message.success('Asset deleted successfully');
      fetchAssets();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete asset');
    }
  };

  const cardHandlers: AssetListCardHandlers = {
    onEdit: handleEdit,
    onDelete: handleDelete,
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Asset) => (
        <Link to={`/admin/assets/${record.id}`}>{name}</Link>
      ),
    },
    {
      title: 'Organization',
      dataIndex: 'org_name',
      key: 'org_name',
      render: (name: string) => name || '-',
    },
    {
      title: 'Type',
      dataIndex: 'asset_type',
      key: 'asset_type',
      render: (type: string) => (type ? <Tag>{type}</Tag> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Asset) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete asset"
            description="Are you sure you want to delete this asset?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <PageHeader
        title="Assets"
        actions={
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Create Asset
            </Button>
            <PageHelp
              title="Assets Help"
              sections={[
                {
                  title: 'What are Assets?',
                  content:
                    'Assets represent yachts, vessels, or equipment that have SIM cards and crew assigned to them.\n\nEach asset belongs to a Customer organization.',
                },
                {
                  title: 'Managing Assets',
                  content:
                    'Click an asset name to view its details: assigned SIM cards with usage data, crew members, and configuration.\n\nUse "Create Asset" to add a new yacht. Choose the owning organization and type.',
                  tips: [
                    'SIMs are assigned to assets via the Inventory page',
                    'Crew is managed from the Asset Detail page',
                  ],
                },
              ]}
            />
          </>
        }
      />

      <ResponsiveTableOrCards
        dataSource={assets}
        rowKey={record => record.id}
        columns={columns}
        loading={loading}
        breakpoint="xl"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        renderCard={asset => <AssetListCard asset={asset} handlers={cardHandlers} />}
        emptyText="No assets found"
      />

      {/* Create Modal */}
      <ResponsiveModal
        title="Create Asset"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter asset name' }]}
          >
            <Input placeholder="Asset name" />
          </Form.Item>
          <Form.Item
            name="organizationId"
            label="Organization"
            tooltip="Yachts (and other physical assets) belong to the end customer that operates them. Platform and reseller orgs are filtered out."
            rules={[{ required: true, message: 'Please select an organization' }]}
          >
            <Select
              placeholder="Select customer organization"
              showSearch
              optionFilterProp="children"
            >
              {organizations
                .filter(org => org.type === 'customer' && org.is_active)
                .map(org => (
                  <Select.Option key={org.id} value={org.id}>
                    {org.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="asset_type" label="Type">
            <LookupSelect category="asset_type" placeholder="Select or enter asset type" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Create
              </Button>
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>

      {/* Edit Modal */}
      <ResponsiveModal
        title={`Edit Asset: ${editingAsset?.name || ''}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingAsset(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter asset name' }]}
          >
            <Input placeholder="Asset name" />
          </Form.Item>
          <Form.Item name="asset_type" label="Type">
            <LookupSelect category="asset_type" placeholder="Select or enter asset type" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Save
              </Button>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingAsset(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </div>
  );
};

export default AssetList;
